import React, { useState } from 'react';
import { Alert, DatePicker, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { getSearchQueries } from '../api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Журнал поиска по сайту: что ищут и есть ли это в ассортименте
const AVAILABILITY = {
  in_stock: { label: 'В наличии', color: 'green' },
  out_of_stock: { label: 'Нет в наличии', color: 'orange' },
  partial: { label: 'Найдено частично', color: 'gold' },
  not_in_assortment: { label: 'Нет в ассортименте', color: 'red' },
};

const SOURCES = {
  header: 'Шапка',
  page: 'Страница поиска',
  legacy: 'Старый сайт',
  unknown: 'Неизвестно',
};

const toOptions = (map) => Object.entries(map).map(([value, item]) => ({ value, label: item.label || item }));

const columns = [
  {
    title: 'Дата',
    dataIndex: 'createdAt',
    width: 140,
    render: (value) => moment(value).format('DD.MM.YYYY HH:mm'),
  },
  {
    title: 'Запрос',
    dataIndex: 'query',
    render: (query, row) => (
      <Space direction="vertical" size={0}>
        {row.typedVariants?.length > 1 ? (
          <Tooltip title={row.typedVariants.join(' → ')}>
            <Text strong>{query}</Text>
          </Tooltip>
        ) : (
          <Text strong>{query}</Text>
        )}
        {row.correctedQuery ? <Text type="secondary">раскладка: {row.correctedQuery}</Text> : null}
        {row.unmatchedTokens?.length ? (
          <Text type="secondary">не найдено: {row.unmatchedTokens.join(', ')}</Text>
        ) : null}
      </Space>
    ),
  },
  {
    title: 'Результат',
    dataIndex: 'availability',
    width: 170,
    render: (value) =>
      AVAILABILITY[value] ? <Tag color={AVAILABILITY[value].color}>{AVAILABILITY[value].label}</Tag> : '—',
  },
  {
    title: 'Найдено / в наличии',
    dataIndex: 'foundCount',
    width: 110,
    render: (value, row) => `${value ?? 0} / ${row.inStockCount ?? 0}`,
  },
  {
    title: 'Клики',
    dataIndex: 'clicks',
    width: 80,
    render: (clicks) => clicks?.length || 0,
  },
  {
    title: 'Источник',
    dataIndex: 'source',
    width: 140,
    render: (value) => SOURCES[value] || value,
  },
  {
    title: 'Клиент',
    dataIndex: 'client',
    width: 220,
    render: (client, row) =>
      client ? (
        <Space direction="vertical" size={0}>
          <Text>{client.legalName || client.name}</Text>
          {client.inn ? <Text type="secondary">ИНН {client.inn}</Text> : null}
        </Space>
      ) : row.clientId ? (
        <Text type="secondary">сотрудник</Text>
      ) : (
        <Text type="secondary">аноним</Text>
      ),
  },
];

const SearchQueries = () => {
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 50 });

  const params = {
    ...filters,
    page: pagination.page,
    limit: pagination.limit,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['searchQueries', params],
    queryFn: () => getSearchQueries(params),
    keepPreviousData: true,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDates = (dates) => {
    setFilters((prev) => ({
      ...prev,
      from: dates?.[0] ? dates[0].startOf('day').toISOString() : undefined,
      to: dates?.[1] ? dates[1].endOf('day').toISOString() : undefined,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Журнал поиска</Title>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Текст запроса"
          style={{ width: 240 }}
          onSearch={(value) => setFilter('q', value.trim())}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Результат"
          style={{ minWidth: 220 }}
          options={toOptions(AVAILABILITY)}
          onChange={(values) => setFilter('availability', values.join(','))}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Источник"
          style={{ minWidth: 200 }}
          options={toOptions(SOURCES)}
          onChange={(values) => setFilter('source', values.join(','))}
        />
        <Select
          allowClear
          placeholder="Клики"
          style={{ width: 150 }}
          options={[
            { value: 'true', label: 'С кликами' },
            { value: 'false', label: 'Без кликов' },
          ]}
          onChange={(value) => setFilter('clicked', value)}
        />
        <RangePicker format="DD.MM.YYYY" onChange={handleDates} />
      </Space>

      {error ? (
        <Alert
          type="error"
          style={{ marginBottom: 16 }}
          message={error?.response?.data?.error || error?.response?.data?.message || 'Ошибка загрузки журнала'}
        />
      ) : null}

      <Table
        rowKey="_id"
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data || []}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: data?.allCount || 0,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100, 200],
          showTotal: (total) => `Всего: ${total}`,
          onChange: (page, limit) => setPagination({ page, limit }),
        }}
      />
    </div>
  );
};

export default SearchQueries;
